import { useState, useEffect, useRef, useCallback } from 'react';
import { loadGoogleMapsScript } from '../utils/loadGoogleMapsScript';
import { buildStateSuggestion } from '../utils/searchScopes';

// Use Google Maps JavaScript API (Places) — new AutocompleteSuggestion API.
// Requests go to maps.googleapis.com, not through our app/Firebase.
const DEBOUNCE_MS = 220;
const MIN_LENGTH = 2;

const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

/**
 * Normalize new API PlacePrediction to the shape the app expects (same as legacy AutocompleteService).
 */
function normalizePlacePrediction(placePrediction) {
  if (!placePrediction) return null;
  const main = placePrediction.mainText?.text ?? '';
  const secondary = placePrediction.secondaryText?.text ?? '';
  const description = placePrediction.text?.text ?? `${main}${secondary ? `, ${secondary}` : ''}`.trim();
  return {
    place_id: placePrediction.placeId || `query-${main}-${secondary}`,
    description,
    structured_formatting: {
      main_text: main,
      secondary_text: secondary,
    },
    _types: Array.isArray(placePrediction.types) ? placePrediction.types : [],
  };
}

const HEALTHCARE_TYPE_HINTS = ['hospital', 'doctor', 'health', 'pharmacy'];
const HEALTHCARE_PRIMARY_TYPES = ['hospital'];
const HEALTHCARE_TEXT_HINTS = [
  'hospital',
  'medical center',
  'medical ctr',
  'health center',
  'healthcare',
  'clinic',
  'urgent care',
  'er',
  'emergency room',
];

function isHealthcareSuggestion(suggestion) {
  if (!suggestion) return false;
  const types = Array.isArray(suggestion._types) ? suggestion._types.map((t) => String(t).toLowerCase()) : [];
  if (types.some((t) => HEALTHCARE_TYPE_HINTS.some((hint) => t.includes(hint)))) return true;

  const haystack = `${suggestion.structured_formatting?.main_text || ''} ${suggestion.description || ''}`.toLowerCase();
  return HEALTHCARE_TEXT_HINTS.some((hint) => haystack.includes(hint));
}

function looksLikeLocalityMatch(suggestion, input) {
  const trimmed = String(input || '').trim().toLowerCase();
  if (!trimmed) return false;
  const main = String(suggestion?.structured_formatting?.main_text || '').trim().toLowerCase();
  const secondary = String(suggestion?.structured_formatting?.secondary_text || '').trim().toLowerCase();
  const types = Array.isArray(suggestion?._types) ? suggestion._types.map((t) => String(t).toLowerCase()) : [];

  const isLocalityType = types.some((t) =>
    t.includes('locality') ||
    t.includes('administrative_area_level_3') ||
    t.includes('administrative_area_level_2') ||
    t.includes('postal_town')
  );

  if (main === trimmed && secondary) return true;
  if (isLocalityType && (main === trimmed || main.startsWith(`${trimmed},`) || main.startsWith(`${trimmed} `))) return true;
  return false;
}

function sortSuggestions(input, suggestions, prioritizeHealthcare) {
  return [...suggestions].sort((a, b) => {
    const aLocality = looksLikeLocalityMatch(a, input) ? 0 : 1;
    const bLocality = looksLikeLocalityMatch(b, input) ? 0 : 1;
    if (aLocality !== bLocality) return aLocality - bLocality;

    if (prioritizeHealthcare) {
      const aHealthcare = isHealthcareSuggestion(a) ? 0 : 1;
      const bHealthcare = isHealthcareSuggestion(b) ? 0 : 1;
      if (aHealthcare !== bHealthcare) return aHealthcare - bHealthcare;
    }

    const aMain = String(a?.structured_formatting?.main_text || a?.description || '').toLowerCase();
    const bMain = String(b?.structured_formatting?.main_text || b?.description || '').toLowerCase();
    return aMain.localeCompare(bMain);
  });
}

/**
 * @param {string} inputValue - User input to get predictions for
 * @param {{ types?: string[] }} [options] - Optional. types: undefined (default = addresses + place names) or ['establishment'] for stores only
 */
export function usePlacesAutocomplete(inputValue, options = {}) {
  const typesRef = useRef(options.types);
  typesRef.current = options.types;
  const prioritizeHealthcareRef = useRef(Boolean(options.prioritizeHealthcare));
  prioritizeHealthcareRef.current = Boolean(options.prioritizeHealthcare);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const placesLibRef = useRef(null);
  const debounceRef = useRef(null);
  const requestRef = useRef(0);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    if (!API_KEY) return;
    loadGoogleMapsScript(API_KEY)
      .then((placesLib) => {
        placesLibRef.current = placesLib;
        // Create one session token for the lifetime of this autocomplete instance.
        // Reusing the token groups all keystrokes + the final place selection into
        // a single billing session instead of billing each keystroke separately.
        if (placesLib?.AutocompleteSessionToken) {
          sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        }
        setIsReady(true);
      })
      .catch(() => setIsReady(false));
  }, []);

  const fetchPredictions = useCallback((input) => {
    const types = typesRef.current;
    if (input.length < MIN_LENGTH) {
      setSuggestions([]);
      return;
    }
    const lib = placesLibRef.current;
    if (!lib?.AutocompleteSuggestion) {
      setSuggestions([]);
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    const { AutocompleteSuggestion } = lib;
    const request = {
      input: input.trim(),
      includedRegionCodes: ['us'],
      sessionToken: sessionTokenRef.current,
    };

    const shouldPrioritizeHealthcare = prioritizeHealthcareRef.current;

    const generalPromise = (() => {
      const generalRequest = { ...request };
      if (types && types.length > 0) generalRequest.includedPrimaryTypes = types;
      return AutocompleteSuggestion.fetchAutocompleteSuggestions(generalRequest)
        .then(({ suggestions: rawSuggestions }) => rawSuggestions || [])
        .catch(() => []);
    })();

    const healthcarePromise = shouldPrioritizeHealthcare
      ? (() => {
          const healthcareRequest = {
            ...request,
            includedPrimaryTypes: HEALTHCARE_PRIMARY_TYPES,
          };
          return AutocompleteSuggestion.fetchAutocompleteSuggestions(healthcareRequest)
            .then(({ suggestions: rawSuggestions }) => rawSuggestions || [])
            .catch(() => []);
        })()
      : Promise.resolve([]);

    Promise.all([healthcarePromise, generalPromise])
      .then(([healthcareRaw, generalRaw]) => {
        if (requestId !== requestRef.current) return;
        setLoading(false);

        const healthcareList = healthcareRaw
          .map((s) => (s.placePrediction ? normalizePlacePrediction(s.placePrediction) : null))
          .filter(Boolean);
        const generalList = generalRaw
          .map((s) => (s.placePrediction ? normalizePlacePrediction(s.placePrediction) : null))
          .filter(Boolean);

        const seen = new Set();
        const merged = [];
        [...healthcareList, ...generalList].forEach((item) => {
          if (!item?.place_id || seen.has(item.place_id)) return;
          seen.add(item.place_id);
          merged.push(item);
        });

        const sorted = sortSuggestions(input, merged, shouldPrioritizeHealthcare);
        const stateSuggestion = buildStateSuggestion(input);
        if (stateSuggestion && !sorted.some((item) => item.description?.toLowerCase() === stateSuggestion.description.toLowerCase())) {
          setSuggestions([stateSuggestion, ...sorted]);
          return;
        }
        setSuggestions(sorted);
      })
      .catch(() => {
        if (requestId !== requestRef.current) return;
        setLoading(false);
        setSuggestions([]);
      });
  }, []);

  useEffect(() => {
    if (!isReady) return;
    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_LENGTH) {
      setSuggestions([]);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      fetchPredictions(trimmed);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, isReady, fetchPredictions]);

  // Call after a suggestion is selected to start a fresh billing session for the next search.
  const resetSession = useCallback(() => {
    const lib = placesLibRef.current;
    if (lib?.AutocompleteSessionToken) {
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
    }
  }, []);

  return { suggestions, loading, isReady, resetSession };
}
